import { useEffect, useMemo } from 'react';
import { Chip, CircularProgress, TableCell, TableRow, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { IWorkspacePlanModel } from 'api/models/response/workspacePlan/IWorkspacePlanModel';
import {
    EMPTY_PLAN_FORM,
    UNLIMITED,
    WorkspacePlanFormValues,
    bytesToGbString,
    createWorkspacePlanSchema,
    planToFormValues,
} from './workspacePlanForm';
import {
    ChipsContainer,
    CodeCell,
    FullWidthTextField,
    LimitCell,
    NameCell,
    RowActions,
    RowCancelButton,
    RowDeleteButton,
    RowEditButton,
    RowSaveButton,
    StorageCell,
} from './SubscriptionPlan.styles';

interface IWorkspacePlanRowProps {
    plan?: IWorkspacePlanModel;
    rowKey: string;
    isCurrent?: boolean;
    isEditing: boolean;
    canEdit: boolean;
    isSaving: boolean;
    isDeleting?: boolean;
    onEdit?: () => void;
    onDelete?: () => void;
    onCancel: () => void;
    onSave: (values: WorkspacePlanFormValues) => Promise<boolean>;
}

type FieldOptions = { type?: 'text' | 'number'; step?: string; required?: boolean; autoFocus?: boolean };

export const WorkspacePlanRow = ({
    plan,
    rowKey,
    isCurrent = false,
    isEditing,
    canEdit,
    isSaving,
    isDeleting = false,
    onEdit,
    onDelete,
    onCancel,
    onSave,
}: IWorkspacePlanRowProps) => {
    const { t } = useTranslation();
    const isNew = !plan;
    const initialValues = useMemo(() => (plan ? planToFormValues(plan) : EMPTY_PLAN_FORM), [plan]);

    const { control, handleSubmit, reset, formState: { isDirty, isValid, isSubmitted } } = useForm<WorkspacePlanFormValues>({
        resolver: zodResolver(createWorkspacePlanSchema(t)),
        mode: 'onChange',
        defaultValues: initialValues,
    });

    useEffect(() => {
        if (isEditing) {
            reset(initialValues);
        }
    }, [isEditing, initialValues, reset]);

    const onSubmit = async (values: WorkspacePlanFormValues) => {
        await onSave(values);
    };

    const formatLimit = (value: number) => (value === UNLIMITED ? t('subscriptionPlan.unlimited') : String(value));

    const renderField = (field: keyof WorkspacePlanFormValues, displayValue: string, options: FieldOptions = {}) => {
        if (!isEditing) {
            return <Typography variant="body2" title={displayValue}>{displayValue}</Typography>;
        }

        return (
            <Controller
                name={field}
                control={control}
                render={({ field: controllerField, fieldState }) => (
                    <FullWidthTextField
                        {...controllerField}
                        size="small"
                        type={options.type ?? 'text'}
                        required={options.required}
                        autoFocus={options.autoFocus}
                        disabled={isSaving}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        inputProps={{ step: options.step, 'data-testid': `plan-${rowKey}-${field}` }}
                    />
                )}
            />
        );
    };

    const saveDisabled = isNew ? isSubmitted && !isValid : !isValid || !isDirty;

    const editButtons = (
        <>
            <RowSaveButton
                variant="outlined"
                color="primary"
                loading={isSaving}
                disabled={saveDisabled}
                onClick={handleSubmit(onSubmit)}
                data-testid={`plan-${rowKey}-save`}
            >
                {t('general.save')}
            </RowSaveButton>
            <RowCancelButton
                variant="outlined"
                color="secondary"
                disabled={isSaving}
                onClick={onCancel}
                data-testid={`plan-${rowKey}-cancel`}
            >
                {t('general.cancel')}
            </RowCancelButton>
        </>
    );

    return (
        <TableRow selected={isCurrent} data-testid={`plan-row-${rowKey}`}>
            <NameCell>{renderField('name', plan?.name ?? '', { required: true, autoFocus: true })}</NameCell>
            <CodeCell>{renderField('code', plan?.code ?? '')}</CodeCell>
            <StorageCell>
                {renderField('storageLimitGb', plan ? `${bytesToGbString(plan.storageLimitBytes)} GB` : '', {
                    type: 'number',
                    step: '0.1',
                    required: true,
                })}
            </StorageCell>
            <LimitCell>
                {renderField('maxUsers', plan ? formatLimit(plan.maxUsers) : '', { type: 'number', step: '1', required: true })}
            </LimitCell>
            <LimitCell>
                {renderField('maxGogglesCount', plan ? formatLimit(plan.maxGogglesCount) : '', { type: 'number', step: '1', required: true })}
            </LimitCell>
            <TableCell>
                {plan && (
                    <ChipsContainer>
                        <Chip
                            size="small"
                            label={plan.isSystem ? t('subscriptionPlan.system') : t('subscriptionPlan.custom')}
                            color={plan.isSystem ? 'default' : 'info'}
                        />
                        {isCurrent && <Chip size="small" label={t('subscriptionPlan.current')} color="success" />}
                    </ChipsContainer>
                )}
            </TableCell>
            <TableCell align="right">
                <RowActions>
                    {isEditing && editButtons}
                    {!isEditing && plan && !plan.isSystem && (
                        <>
                            <RowEditButton
                                variant="text"
                                disabled={!canEdit || isDeleting}
                                onClick={onEdit}
                                data-testid={`plan-${rowKey}-edit`}
                            >
                                {t('general.edit')}
                            </RowEditButton>
                            <RowDeleteButton
                                aria-label={t('subscriptionPlan.delete')}
                                disabled={!canEdit || isCurrent || isDeleting}
                                onClick={onDelete}
                                data-testid={`plan-${rowKey}-delete`}
                            >
                                {isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                            </RowDeleteButton>
                        </>
                    )}
                </RowActions>
            </TableCell>
        </TableRow>
    );
};
